import { CheckOutlined, HistoryOutlined, MailOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";

type ReportSendStatusProps = {
	/** Report generated for the period (optional; when omitted only send state is shown). */
	generated?: boolean;
	sent?: boolean;
	sentTo?: string[] | Set<string> | null;
	failedTo?: string[] | Set<string> | null;
	/** Expected recipients (e.g. ReportConfig.emails). Falls back to sent + failed. */
	total?: number;
	compact?: boolean;
};

const toList = (value?: string[] | Set<string> | null): string[] => {
	if (!value) return [];
	return Array.isArray(value) ? value : Array.from(value);
};

const badgeStyle = (compact: boolean, colors: { color: string; background: string }) => ({
	display: "inline-flex" as const,
	alignItems: "center" as const,
	gap: 3,
	fontSize: compact ? 10 : 12,
	fontWeight: 600,
	padding: compact ? "0px 5px" : "1px 6px",
	borderRadius: 4,
	lineHeight: compact ? "16px" : "18px",
	...colors,
});

export const ReportSendStatus = ({
	generated,
	sent,
	sentTo,
	failedTo,
	total: expectedTotal,
	compact = false,
}: ReportSendStatusProps) => {
	const recipients = toList(sentTo);
	const failures = toList(failedTo);
	const attempted = recipients.length + failures.length;
	const total = expectedTotal && expectedTotal > 0 ? expectedTotal : attempted;
	const partial = recipients.length < total;

	if (generated === false) {
		return (
			<span style={badgeStyle(compact, { color: "#b45309", background: "#ffedd5" })}>
				<HistoryOutlined />
				Pendiente
			</span>
		);
	}

	if (!sent) {
		if (generated === true) {
			return (
				<span style={badgeStyle(compact, { color: "#15803d", background: "#dcfce7" })}>
					<CheckOutlined />
					Generado
				</span>
			);
		}
		return (
			<span style={badgeStyle(compact, { color: "#64748b", background: "#f1f5f9" })}>
				<MailOutlined />
				No enviado
			</span>
		);
	}

	const label = `Enviado ${recipients.length} de ${total || recipients.length}`;

	const tooltipLines = [
		recipients.length ? `Destinatarios:\n${recipients.map((email) => `• ${email}`).join("\n")}` : null,
		failures.length ? `Fallidos:\n${failures.map((email) => `• ${email}`).join("\n")}` : null,
	].filter(Boolean);

	const content = (
		<span style={{
			...badgeStyle(compact, partial
				? { color: "#b45309", background: "#ffedd5" }
				: { color: "#1d4ed8", background: "#dbeafe" }),
			cursor: tooltipLines.length ? "default" : undefined,
		}}>
			<MailOutlined />
			{label}
		</span>
	);

	if (!tooltipLines.length) {
		return content;
	}

	return (
		<Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{tooltipLines.join("\n")}</div>}>
			{content}
		</Tooltip>
	);
};
